import { GetStaticPaths, GetStaticProps } from "next/types";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useState, Fragment, useMemo } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { Dialog, Disclosure, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FunnelIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";

import { recent } from "../../queries/articles";
// import { featuredProducts } from '../../queries/products'
import { servicesByCategory, servicesBySearch } from "../../queries/services";
import ApiClient from "../../utils/client";

const Layout = dynamic(() => import("../../components/layout"));
const Articles = dynamic(
  () => import("../../components/organisms/blog-content")
);
// const Products = dynamic(
//   () => import('../../components/organisms/product-content')
// )
const Markdown = dynamic(() => import("../../components/molecules/markdown"));

const offers = [
  {
    name: "Laser Hair Removal",
    description: "Get up to 80% off courses",
    href: "/laser-hair-removal/",
  },
  {
    name: "Treatment Plans",
    description: "Save On Individual Sessions With Plans",
    href: "/price-list/",
  },
  {
    name: "Price Match Guarantee",
    description: "Unbeatable Prices Guaranteed",
    href: "/price-list/",
  },
];

const Meta = {
  body: {
    title: "Advanced Body Treatments | Soluna London",
    description:
      "Explore a wide range of body treatments at Soluna London, from weight loss and toning to skin rejuvenation and more! Book a free consultation today online or call 0333 016 3500",
  },
  face: {
    title: "Advanced Face & Skin Treatments | Soluna London",
    description:
      "Soluna London offers a wide range of advanced face and skin treatments to help you achieve your skin goals. Book a free consultation today online or call 0333 016 3500",
  },
};

const Content = {
  body: `# Advanced Body Treatments at Soluna London\n\nAt Soluna London, we offer a comprehensive range of advanced body treatments designed to help you look and feel your best. Our treatments are tailored to meet the unique needs of each client, ensuring optimal results.\n\n### [Powersculpt - Burn Fat and Tone Muscle](https://solunalondon.com/powersculpt/)\n\nOur Powersculpt treatment is a revolutionary approach to body shaping and muscle toning. In just one 30-minute session, you can achieve the equivalent of 20,000 crunches, squats, curls, or leg extensions. This powerful treatment is designed to burn fat and tone muscles, helping you achieve a more sculpted physique.\n\n### [3D Bespoke Concentrated Non-Surgical Fat Removal](https://solunalondon.com/3D-bespoke-non-surgical-fat-removal/)\n\nFor those looking to target specific areas of concern, our 3D Bespoke Concentrated Non-Surgical Fat Removal treatment offers a concentrated approach to body contouring. This treatment combines the power of 3D-Cavitation, 3D-Radio Frequency, and 3D-Shockwave to provide effective and safe fat reduction.\n\n### [Dermaforce Microneedling + Radiofrequency](https://solunalondon.com/dermaforce/)\n\nOur Dermaforce treatment combines the power of microneedling and radiofrequency technology to rejuvenate the skin. This non-surgical treatment is effective in addressing a wide range of skin concerns, delivering impressive results with minimal downtime.\n\n### [Ultimate HIFU Body](https://solunalondon.com/hifu-body/)\n\nThe Ultimate HIFU (High Intensity Focused Ultrasound) Body treatment is a non-surgical technology designed to target stubborn pockets of fat. This treatment offers a fast and effective solution for body sculpting, helping you achieve the body contours you desire.\n\n### [Manual Lymphatic Drainage](https://solunalondon.com/manual-lymphatic-drainage/)\n\nOur Manual Lymphatic Drainage treatment uses gentle and precise strokes to encourage the flow of lymph, helping to reduce swelling, congestion, and pain. This treatment is ideal for those looking to detoxify their body and boost their overall wellbeing.\n\n### [Nonsurgical Bum Lift](https://solunalondon.com/nonsurgical-bum-lift/)\n\nOur Nonsurgical Bum Lift is a popular treatment designed to enhance the shape and contour of your behind. Using clinically proven technology, this treatment delivers impressive results without the need for surgery.\n\n### [Hydro Facial Back](https://solunalondon.com/back-facial/) and [Bum Facials](https://solunalondon.com/bum-facial/)\n\nOur Hydro Facial Back and Bum Facials are designed to rejuvenate your skin, leaving it looking and feeling refreshed and revitalized. These treatments use a combination of exfoliation and cleansing to renew your skin.\n\n### [Body Peels](https://solunalondon.com/body-peels/)\n\nOur Body Peels use naturally occurring acids and active ingredients to deeply exfoliate and brighten the skin. This treatment stimulates new cell growth and collagen production, revealing fresher, clearer, and brighter skin.\n\n### [Aqualyx Fat Dissolving](https://solunalondon.com/aqualyx-fat-dissolving/)\n\nAqualyx is an effective fat removal and body sculpting procedure. It's one of the most popular treatments at Soluna London, known for its safe and effective results.`,
  face: `# Advanced Face Treatments at Soluna London\n\nAt Soluna London, we pride ourselves on offering a diverse range of advanced face treatments. Our treatments are designed to address a variety of skin concerns, helping you achieve a healthier, more youthful complexion.\n\n### [Plasma Fibroblast Skin Tightening](https://solunalondon.com/plasma-fibroblast/)\n\nExperience the power of our Plasma Fibroblast Skin Tightening treatment. This innovative procedure is designed for eyelid lifting and tightening, wrinkle reduction, neck tightening, upper lip wrinkles, and scar reduction.\n\n### [Radio Frequency Facial](https://solunalondon.com/radio-frequency-facial/)\n\nOur Radio Frequency Facial is an efficient treatment for skin tightening, anti-ageing, and contouring. This treatment leaves your skin with a healthier, firmer appearance.\n\n### [Skin Booster Facial](https://solunalondon.com/skin-booster-facial/)\n\nThe Skin Booster Facial at Soluna London is a unique combination of an injectable skin booster and an advanced facial protocol. This treatment is designed to rejuvenate your skin, leaving it looking and feeling its best.\n\n### [Skin Peel](https://solunalondon.com/skin-peels/)\n\nOur Skin Peels is a chemical skin peel that exfoliates the skin, encourages it to refresh, and stimulates the production of new skin cells. This treatment is designed to stimulate your natural collagen, leaving your skin looking younger and healthier.\n\n### [Mesotherapy Infusion](https://solunalondon.com/mesotherapy-infusion/)\n\nOur Mesotherapy Infusion treatment uses a combination of vitamins and minerals to treat fine lines, wrinkles, dry skin, cellulite, and stretch marks. This treatment leaves your skin looking and feeling rejuvenated.\n\n### [HydroFacial](https://solunalondon.com/hydrafacial/)\n\nExperience the luxury of our HydroFacial treatment. This medical-grade facial rejuvenation treatment is designed to cleanse, hydrate, and leave your skin looking radiant.\n\n### [Hollywood Facial](https://solunalondon.com/hollywood-facial/)\n\nOur Hollywood Facial is a luxurious facial where science meets indulgence. This bespoke facial combines multiple techniques to take care of all your skincare needs.\n\n### [HIFU Face Lift](https://solunalondon.com/hifu/)\n\nThe HIFU (High Intensity Focused Ultrasound) Face Lift is a non-surgical technology known within the industry as the “No Knife Facelift”. This treatment provides immediate visible tightening and lifting effects, with results lasting several months.\n\n### [Dracula Skin Therapy](https://solunalondon.com/dracula-skin-therapy/)\n\nOur Dracula Skin Therapy is a replenishing healing treatment using your own natural resources. This treatment is effective for addressing concerns such as crow's feet, acne scars, and more.\n\n### [MCA Scar Therapy](https://solunalondon.com/mca-scar-therapy/)\n\nMCA Scar Therapy is a fantastic treatment for anyone looking to improve the appearance and feel of scars, burns, and stretch marks.\n\n### [Microneedling](https://solunalondon.com/microneedling/)\n\nOur Microneedling treatment uses very tiny needles to stimulate a healing response. This treatment is suitable for all skin types and leaves your skin looking and feeling rejuvenated.\n\n### [BB Glow Foundation](https://solunalondon.com/bbglow-foundation/)\n\nThe BB Glow Foundation treatment provides naturally glowing skin with a smooth complexion without the need for makeup.\n\n### [Deep Cleanse Facial](https://solunalondon.com/deep-cleanse-facial/)\n\nOur Deep Cleanse Facial is the ultimate deep cleansing facial for deep-rooted congested skin. This treatment cleanses with steam followed by a superficial exfoliation, leaving your skin feeling clean and refreshed.\n\n### [LED Light Therapy + Hyaluronic Acid Infusion](https://solunalondon.com/led-light-therapy/)\n\nOur LED Light Therapy + Hyaluronic Acid Infusion treatment is a revolutionary new technique used in skin and facial treatments. This treatment leaves your skin looking and feeling rejuvenated.\n\n### [Dermaplaning](https://solunalondon.com/dermaplaning/)\n\nOur Dermaplaning treatment gently buffs the top layer of dead skin cells away to reveal a brighter and smoother complexion.\n\n### [Advanced Facial Consultation + SkinCeuticals SkinScope LED](https://solunalondon.com/advanced-facial-consultation/)\n\nOur Advanced Facial Consultation includes a SkinScope LED diagnostic evaluation. Our expert consultations with an experienced aesthetician will answer all of your questions and give you expert advice to improve your skin.`,
};

export default function Treatments(props) {
  const router = useRouter();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    try {
      const formatted =
        props.type &&
        (props.type as string)
          .replace(/-/g, " ")
          .replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match.toUpperCase());

      // @ts-ignore
      klaviyo &&
        // @ts-ignore
        klaviyo.push([
          "track",
          "View",
          {
            Category: formatted,
            URL: window.location.href,
          },
        ]);

      window.gtag &&
        window.gtag("event", "view_item_list", {
          item_list_name: `${formatted} Treatments`,
          items:
            props.content &&
            props.content.map((service) => ({
              id: service.id,
              item_id: service.id,
              name: service.title,
              item_name: service.title,
              price:
                service.pricing[service.pricing.length - 1].data.price / 100,
            })),
        });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  }, []);

  useEffect(() => {
    const filter = [];
    const keys = {};

    props.content &&
      props.content.forEach((service) => {
        const { ratings, qualities, ...rest } = service.attributes;
        Object.keys(rest).forEach((key) => {
          const coll = rest[key];
          const ex = filter.find((f) => f.id === key);
          if (!keys[key]) {
            keys[key] = [...coll];
          } else {
            keys[key] = [...keys[key], ...coll];
          }
        });
      });

    Object.keys(keys).forEach((key) => {
      const coll = [...new Set(keys[key])];
      const ex = filter.find((f) => f.id === key);
      if (!ex) {
        filter.push({
          id: key,
          name: `Filter by ${key.replace(/-/g, " ")}`,
          options: coll.map((item: string) => {
            return {
              value: item,
              label: item.replace(/-/g, " "),
              checked: false,
            };
          }),
        });
      }
    });
    setFilters(filter);
  }, [router.asPath]);

  const opts = useMemo(() => {
    if (props.content && props.content.length > 0) {
      return props.content.filter((service) => {
        const { area, concern } = service.attributes;
        if (selectedAreas.length > 0 && selectedConcerns.length > 0) {
          const a = selectedAreas.some((r) => area && area.includes(r));
          const c = selectedConcerns.some(
            (r) => concern && concern.includes(r)
          );
          return a && c;
        } else if (selectedAreas.length > 0) {
          return selectedAreas.some((r) => area && area.includes(r));
        } else if (selectedConcerns.length > 0) {
          return selectedConcerns.some((r) => concern && concern.includes(r));
        }
        return true;
      });
    }
    return [];
  }, [props.content, selectedAreas, selectedConcerns]);

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const { type } = router.query;

  const formatted =
    props.type &&
    (props.type as string)
      .replace(/-/g, " ")
      .replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match.toUpperCase());

  const handleSelectedArea = (area) => {
    if (selectedAreas.includes(area)) {
      setSelectedAreas(selectedAreas.filter((a) => a !== area));
    } else {
      setSelectedAreas([...selectedAreas, area]);
    }
  };

  const handleSelectedConcern = (concern) => {
    if (selectedConcerns.includes(concern)) {
      setSelectedConcerns(selectedConcerns.filter((c) => c !== concern));
    } else {
      setSelectedConcerns([...selectedConcerns, concern]);
    }
  };

  const meta = Meta[props.type] || {
    title: `${formatted} Treatments | Soluna London`,
    description: `Discover our range of ${formatted} treatments at Soluna London. Book your free consultation today on 0333 016 3500`,
  };

  const content = Content[props.type] || null;

  return (
    <Layout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link
          rel="canonical"
          href={`https://solunalondon.com/treatments/${props.type}/`}
        />
        <link
          rel="alternate"
          hrefLang="en-GB"
          href={`https://solunalondon.com/treatments/${props.type}/`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://solunalondon.com/treatments/${props.type}/`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`https://solunalondon.com/treatments/${props.type}/`}
        />
      </Head>
      <div className="bg-white">
        <div>
          {/* Mobile filter dialog */}
          <Transition.Root show={mobileFiltersOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-40 lg:hidden"
              onClose={setMobileFiltersOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>

              <div className="fixed inset-0 z-40 flex">
                <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                    <div className="flex items-center justify-between px-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        {/* Filter by concern */}
                      </h2>
                      <button
                        type="button"
                        className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                        onClick={() => setMobileFiltersOpen(false)}
                      >
                        <span className="sr-only">Close menu</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Filters */}
                    <form className="mt-4 border-t border-gray-200">
                      <h3 className="sr-only">Categories</h3>

                      {filters.map((section) => (
                        <Disclosure
                          as="div"
                          key={section.id}
                          defaultOpen
                          className="border-t border-gray-200 px-4 py-6"
                        >
                          {({ open }) => (
                            <>
                              <h3 className="-mx-2 -my-3 flow-root">
                                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                                  <span className="font-medium text-gray-900">
                                    {section.name}
                                  </span>
                                  <span className="ml-6 flex items-center">
                                    {open ? (
                                      <MinusIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <PlusIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </Disclosure.Button>
                              </h3>
                              <Disclosure.Panel
                                className="pt-6"
                                unmount={false}
                              >
                                <div className="space-y-6">
                                  {section.options.map((option, optionIdx) => (
                                    <div
                                      key={option.value}
                                      className="flex items-center"
                                    >
                                      <input
                                        id={`filter-mobile-${section.id}-${optionIdx}`}
                                        name={`${section.id}[]`}
                                        defaultValue={option.value}
                                        type="checkbox"
                                        defaultChecked={option.checked}
                                        onChange={() => {
                                          if (section.id === "area") {
                                            handleSelectedArea(option.value);
                                          } else {
                                            handleSelectedConcern(option.value);
                                          }
                                        }}
                                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                      />
                                      <label
                                        htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                        className="ml-3 min-w-0 flex-1 text-gray-500 capitalize"
                                      >
                                        {option.label}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      ))}
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>

          <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-baseline justify-between border-b border-gray-200 pb-6 pt-6">
              <h1 className="text-3xl font-serif font-medium text-gray-900 sm:text-4xl">
                {formatted} Treatments
              </h1>

              <div className="flex flex-1 w-full mt-4 lg:hidden lg:mt-0">
                <button
                  type="button"
                  className="p-2 text-gray-600 hover:text-gray-500 sm:ml-6 lg:hidden border rounded-md w-full items-center flex justify-center"
                  onClick={() => setMobileFiltersOpen(true)}
                >
                  <span className="mr-2">Filter by body area or concern</span>
                  <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <section aria-labelledby="products-heading" className="pb-24 pt-6">
              <h2 id="products-heading" className="sr-only">
                Products
              </h2>

              <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
                {/* Filters */}
                <form className="hidden lg:block">
                  <h3 className="sr-only">Categories</h3>
                  {filters.map((section) => (
                    <Disclosure
                      as="div"
                      key={section.id}
                      defaultOpen
                      className="border-b border-gray-200 py-6"
                    >
                      {({ open }) => (
                        <>
                          <h3 className="-my-3 flow-root">
                            <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                              <span className="font-medium text-gray-900">
                                {section.name}
                              </span>
                              <span className="ml-6 flex items-center">
                                {open ? (
                                  <MinusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <PlusIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel className="pt-6" unmount={false}>
                            <div className="space-y-4">
                              {section.options.map((option, optionIdx) => (
                                <div
                                  key={option.value}
                                  className="flex items-center"
                                >
                                  <input
                                    id={`filter-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    defaultValue={option.value}
                                    type="checkbox"
                                    defaultChecked={option.checked}
                                    onChange={() => {
                                      if (section.id === "area") {
                                        handleSelectedArea(option.value);
                                      } else {
                                        handleSelectedConcern(option.value);
                                      }
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                  />
                                  <label
                                    htmlFor={`filter-${section.id}-${optionIdx}`}
                                    className="ml-3 text-sm text-gray-600 capitalize"
                                  >
                                    {option.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </form>

                {/* Product grid */}
                <div className="lg:col-span-3">
                  <section className="relative bg-white pt-6 pb-20 lg:pt-0 lg:pb-12">
                    <div className="max-w-lg mx-auto grid gap-8 sm:grid-cols-2 sm:max-w-4xl lg:grid-cols-2 lg:max-w-7xl lg:mx-auto lg:px-8">
                      {opts.map((service) => {
                        const img = service.media.find(
                          (x) => x.asset.type.value === "IMAGE"
                        );
                        return (
                          <div
                            className="relative flex flex-col rounded-lg shadow-lg overflow-hidden"
                            key={service.id}
                          >
                            <Link href={service.metadata.path} legacyBehavior>
                              <>
                                <div className="relative aspect-1 flex-shrink-0 rounded-lg overflow-hidden shadow">
                                  {img && (
                                    <Image
                                      src={img.asset.url}
                                      className="aspect-1 w-full object-cover"
                                      alt={`${service.title} ${service.metadata.description}`}
                                      fill
                                      sizes="100vw"
                                    />
                                  )}
                                </div>
                                <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-brand-600">
                                      {service.category.name}
                                    </p>
                                    <div className="block mt-2">
                                      <p className="text-xl lg:text-3xl font-serif font-medium text-gray-900">
                                        {service.title}
                                      </p>
                                      <p className="mt-3 text-base text-gray-500 line-clamp-2">
                                        {service.metadata.callout ||
                                          service.content.highlight}
                                      </p>
                                      <p className="text-brand-600 font-medium flex items-center mt-1">
                                        <span>Learn more</span>
                                        <svg
                                          xmlns="https://www.w3.org/2000/svg"
                                          className="h-6 w-6 ml-1"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                                          />
                                        </svg>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <a
                                  href={service.metadata.path}
                                  className="stretch-link"
                                >
                                  <span className="sr-only">
                                    {service.title} -{" "}
                                    {service.metadata.description}
                                  </span>
                                </a>
                              </>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </div>
              </div>
            </section>
          </main>
        </div>
        {content ? (
          <div>
            <Markdown
              className="prose prose-lg text-gray-700 text-base leading-7 mb-6 -mt-20 lg:-mt-8 border-t pt-6 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
              source={content}
              renderers={{
                p: ({ children }) => (
                  <p className="first:text-base first:font-bold first:text-gray-900 text-base text-gray-500">
                    {children}
                  </p>
                ),
                h1: ({ children }) => (
                  <h1 className="text-gray-900 mt-2 text-3xl font-bold tracking-tight sm:text-4xl mb-6">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h1 className="text-gray-900 text-xl">{children}</h1>
                ),
                h3: ({ children }) => (
                  <h1 className="text-gray-900 text-xl mt-6 font-semibold">
                    {children}
                  </h1>
                ),
                a: ({ children, href }) => (
                  <a href={href} className="no-underline">
                    {children}
                  </a>
                ),
              }}
            />
          </div>
        ) : null}
      </div>

      <nav aria-label="Offers" className="order-last lg:order-first border-t">
        <div className="mx-auto max-w-7xl lg:px-8 border-l border-r">
          <ul
            role="list"
            className="grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-3 lg:divide-y-0 lg:divide-x"
          >
            {offers.map((offer) => (
              <li key={offer.name} className="flex flex-col">
                <a
                  href={offer.href}
                  className="relative flex flex-1 flex-col justify-center bg-white py-6 px-4 text-center focus:z-10"
                >
                  <p className="text-sm text-gray-500">{offer.name}</p>
                  <p className="font-semibold text-gray-900">
                    {offer.description}
                  </p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <section className="bg-brand-800">
        <div className="mx-auto max-w-7xl md:grid md:grid-cols-2 md:px-6 lg:px-8">
          <div className="py-12 px-4 sm:px-6 md:flex md:flex-col md:border-r md:border-brand-900 md:py-16 md:pl-0 md:pr-10 lg:pr-16">
            <q className="mt-6 md:flex md:flex-grow md:flex-col">
              <div className="relative text-lg font-medium text-white md:flex-grow">
                <svg
                  className="absolute top-0 left-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-brand-600"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                  aria-hidden="true"
                >
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="relative">
                  Had 3 hydra facials at Soluna London so far and I can&apos;t
                  wait to go back. The facial itself is so relaxing (I almost
                  fall asleep every time) and my skin feels/ looks amazing after
                  but the staff are even better - they are lovely and welcoming
                  and make sure they know exactly what you want out of your
                  experience, can&apos;t recommend Soluna London enough!
                </p>
              </div>
              <footer className="mt-8">
                <div className="flex items-center">
                  <div className="inline-flex flex-shrink-0 rounded-full border-2 border-white">
                    <img
                      className="h-12 w-12 rounded-full"
                      src="https://lh3.googleusercontent.com/a-/ACNPEu95cAGbz8neuozrOy4xVVcT-Fwde3SjltfluHm-Cw=s40-c-c0x00000000-cc-rp-mo-br100"
                      alt=""
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-base font-medium text-white">
                      Jordan O&apos;Driscoll
                    </div>
                  </div>
                </div>
              </footer>
            </q>
          </div>
          <div className="border-t-2 border-brand-900 py-12 px-4 sm:px-6 md:border-t-0 md:border-l md:py-16 md:pr-0 md:pl-10 lg:pl-16">
            <q className="mt-6 md:flex md:flex-grow md:flex-col">
              <div className="relative text-lg font-medium text-white md:flex-grow">
                <svg
                  className="absolute top-0 left-0 h-8 w-8 -translate-x-3 -translate-y-2 transform text-brand-600"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                >
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <p className="relative">
                  Have been receiving facial peels from Soluna London for
                  chickenpox scars that left my face in a bad way. The ladies
                  have been lovely! From the consultation to now the treatments.
                  No one tried to upsell me anything and they&apos;ve been
                  incredibly kind. Happy to be going there!
                </p>
              </div>
              <footer className="mt-8">
                <div className="flex items-center">
                  <div className="inline-flex flex-shrink-0 rounded-full border-2 border-white">
                    <img
                      className="h-12 w-12 rounded-full"
                      src="https://lh3.googleusercontent.com/a-/ACNPEu9tGp2dco6j3sMwXh2Hsr7oL4ymhCQXibRGFDQx0g=s40-c-c0x00000000-cc-rp-mo-br100"
                      alt=""
                    />
                  </div>
                  <div className="ml-4">
                    <div className="text-base font-medium text-white">
                      Majean G
                    </div>
                  </div>
                </div>
              </footer>
            </q>
          </div>
        </div>
      </section>
      {/* {props.products && <Products products={props.products} />} */}
      {props.articles && <Articles articles={props.articles} />}
    </Layout>
  );
}

const categoryMap = {
  face: "Facials",
  "aesthetic-facials": "Facials",
  injectables: "Dermal Fillers",
  body: "Body",
  beauty: "Beauty",
  "iv-therapy-and-booster-shots": "IV Therapy & Booster Shots",
  "dermal-fillers": "Dermal Fillers",
  "semi-permanent-makeup": "Semi Permanent Makeup",
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  const isType = categoryMap[params.type as string];
  const client = ApiClient();

  let query = servicesBySearch;
  let variables: any = {
    search: `%${params.type}%`,
  };

  let exclude = [];

  if (isType) {
    if (isType === "Facials") {
      exclude = [
        // skinceuticals facials as in landing page
        "eda41e2e-0ad1-4b6b-aa47-2f3ac987ddda",
        "2fc481a2-fe67-4403-9e08-0e11c45f85e4",
        "5591325f-e6ea-4e0c-88b6-b9c7a51e9711",
        "e233ecbd-6bfa-4325-bca2-e4a597542d07",
        "a6d73826-e754-4228-ba17-58f6ace64a77",
        "a3e9128c-74e4-47df-9fc4-8e116a53655f",
        "29b77729-af3b-4e5a-8bb2-3368f99336e2",
        // murad as in landing page
        "271896a8-cad3-4267-b085-ef83ab828fd1",
        "5fd51fcf-41ee-44b5-9e66-ea6b1c8d13dc",
        "b2da9dfa-e1ce-4316-a6c2-e9a4ad78237d",
        "da3e2ec7-0f01-4176-bf15-96d3e4fc659b",
        "488c7ad6-076d-4fed-baf1-cb4688f96c49",
      ];
    }

    query = servicesByCategory;
    variables = {
      category: isType,
      exclude,
    };
  }

  let pageData = null;
  let articles = null;
  let products = null;

  try {
    pageData = await client.query({
      query,
      variables,
    });
  } catch (e) {
    Sentry.captureException(e);
  }

  try {
    articles = await client.query({
      query: recent,
    });
  } catch (e) {
    Sentry.captureException(e);
  }

  // try {
  //   products = await client.query({
  //     query: featuredProducts,
  //   })
  // } catch (e) {
  //   Sentry.captureException(e)
  // }

  const props = {
    ...pageData.data,
    type: params.type,
    articles: articles.data.content,
    // products: products.data.content,
  };

  return {
    revalidate: 60 * 60,
    props,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      {
        params: {
          type: "face",
        },
      },
      {
        params: {
          type: "aesthetic-facials",
        },
      },
      {
        params: {
          type: "iv-therapy-and-booster-shots",
        },
      },
      {
        params: {
          type: "body",
        },
      },
      {
        params: {
          type: "weight-loss",
        },
      },
      {
        params: {
          type: "dermal-fillers",
        },
      },
      {
        params: {
          type: "skinceuticals",
        },
      },
      {
        params: {
          type: "semi-permanent-makeup",
        },
      },
      {
        params: {
          type: "skin-boosters",
        },
      },
      {
        params: {
          type: "makeup",
        },
      },
      {
        params: {
          type: "beauty",
        },
      },
      {
        params: {
          type: "laser",
        },
      },
      {
        params: {
          type: "injectables",
        },
      },
    ],
    fallback: true,
  };
};
